import React, { useCallback, useMemo } from 'react';

import cls from 'classnames';

import Autocomplete from '@ott/autocomplete';
import { l10n } from '@ott/l10n';
import Select from '@ott/select';
import type { SelectValue } from '@ott/select';

import { newCardOption, fieldsNames, maskDot, dataLocators } from '../../../constants';
import { transforms, getIssuingNetworkByCardNumber } from '../../../helpers';
import { useCreditCardContext } from '../../../provider';
import { SavedCard, NewCard } from '../../../types';
import { CardNetworkIcon } from './blocks/CardNetworkIcon';
import { Option as CustomOption } from './blocks/Option';

import styles from './CardNumber.scss';

const { parse, format } = transforms[fieldsNames.cardNumber];

export const CardNumber = () => {
  const {
    activeCard,
    cardToDisplay,
    isVirtualCard,
    isSavedCard,
    cards,
    changeFieldValue,
    selectSavedCard,
    selectNewCard,
    checkIfFieldInvalid,
  } = useCreditCardContext();

  const handleAutocompleteValueChange = (cardNumber: string) => {
    if (!cardNumber.includes(maskDot)) {
      changeFieldValue(fieldsNames.cardNumber, cardNumber);
    }
  };

  const handleSelectSavedCard = useCallback(
    (selectedID: SelectValue | null) => {
      if (selectedID === newCardOption.value) {
        selectNewCard();
      } else {
        const card = cards.find(({ id }) => id === selectedID);
        const isSameOption = card?.[fieldsNames.id] === (activeCard as SavedCard)[fieldsNames.id];

        if (card && !isSameOption) {
          selectSavedCard(card);
        }
      }
    },
    [cards, activeCard, selectNewCard, selectSavedCard]
  );

  const options = useMemo(
    () => cards.map((card) => ({ value: card.id, label: card.cardNumber })),
    [cards]
  );

  let CardNumberComponent: React.ReactNode | null = null;

  if (isSavedCard) {
    CardNumberComponent = (
      <Select
        label={l10n('paymentCardNew.numberPlaceholder')}
        value={(activeCard as SavedCard)[fieldsNames.id]}
        options={[...options, newCardOption]}
        onChange={handleSelectSavedCard}
        childrenClassNames={{
          overlayContent: styles.selectOverlay,
          arrow: styles.selectArrow,
        }}
        isDisabled={isVirtualCard}
        CustomOptionRenderer={CustomOption}
        alignType="triggerWidth"
        specificLocator={cls('select', dataLocators.cardNumber)}
      />
    );
  } else {
    CardNumberComponent = (
      <Autocomplete
        label={l10n('paymentCardNew.numberPlaceholder')}
        name={fieldsNames.cardNumber}
        value={(activeCard as SavedCard)[fieldsNames.id]}
        inputValue={(activeCard as NewCard)[fieldsNames.cardNumber]}
        options={options}
        onChange={handleAutocompleteValueChange}
        onSelect={handleSelectSavedCard}
        parse={parse}
        formatter={format}
        isInvalid={checkIfFieldInvalid(fieldsNames.cardNumber)}
        CustomOptionRenderer={CustomOption}
        alignType="triggerWidth"
        inputMode="numeric"
        specificLocator={cls('autocomplete', dataLocators.cardNumber)}
      />
    );
  }

  const issuingNetwork = getIssuingNetworkByCardNumber(cardToDisplay[fieldsNames.cardNumber]);

  return (
    <div className={styles.cardNumber}>
      {CardNumberComponent}

      <div
        className={styles.triggerIssuingNetwork}
        data-locator="issuing-network-icon"
        data-id={issuingNetwork}
      >
        <CardNetworkIcon issuingNetworkType={issuingNetwork} />
      </div>
    </div>
  );
};
